/**
 * Automatic Discounts Module (Local Calculation) - Esprit Colombia Adapted
 *
 * Calculates automatic discount prices from the enriched discount flags Liquid
 * renders in the DOM. No Storefront API shadow carts, no cache, no throttle.
 *
 * Flow:
 * 1. List the product's discount flags via data-discount-type/value attributes
 * 2. Calculate candidate final prices locally from current product price
 * 3. Choose the best valid final price (lowest)
 * 4. Update the price placeholders and leave only the winning flag visible
 *
 * Liquid pre-suppresses the losing flags so the page does not paint all of them
 * and hide the losers a moment later; this module re-ranks them on variant
 * change, where a fixed amount can beat a percentage on a cheaper variant.
 *
 * Esprit Colombia Adaptations:
 * - Card selector: product-card
 * - Price container: [data-price-container] or [ref="priceContainer"]
 * - Current price: [data-original-price] or .price
 * - PDP price container: product-price [ref="priceContainer"]
 */

(function () {
  // ─── Constants ───────────────────────────────────────────────

  var MUTATION_DEBOUNCE = 300; // ms debounce for MutationObserver
  var FORMAT_MONEY_CACHE_MAX = 500; // max entries in formatMoney cache

  // Marks a discount flag hidden because it lost the ranking, not because it
  // does not apply. Liquid stamps it too, on the flags it pre-suppresses.
  var SUPPRESSED_ATTR = "data-discount-suppressed";

  // ─── Module State ───────────────────────────────────────────

  var formatMoneyCache = {}; // persistent cache for formatMoney

  // ─── Helpers ─────────────────────────────────────────────────

  /**
   * Formats a price using the store's money_format.
   * Supports multiple placeholder formats with module-level cache.
   */
  function formatMoney(amountInUnits, moneyFormat) {
    var cacheKey = amountInUnits + "|" + moneyFormat;
    if (formatMoneyCache[cacheKey]) {
      return formatMoneyCache[cacheKey];
    }

    var amount = amountInUnits;
    var formatted = amount.toFixed(2);
    var parts = formatted.split(".");
    var dollars = parts[0];
    var cents = parts[1];

    // EU format: dot thousands, comma decimal
    var withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + cents;

    // US format: comma thousands, dot decimal
    var withDot = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + cents;

    // No decimals
    var rounded = Math.round(amount).toString();
    var roundedWithComma = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    var roundedWithDot = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var roundedWithApostrophe = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    // Swiss format: apostrophe thousands, dot decimal
    var withApostrophe =
      dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + "." + cents;

    var placeholderMap = {
      amount: withDot,
      amount_with_comma_separator: withComma,
      amount_with_apostrophe_separator: withApostrophe,
      amount_no_decimals: roundedWithDot,
      amount_no_decimals_with_comma_separator: roundedWithComma,
      amount_no_decimals_with_apostrophe_separator: roundedWithApostrophe,
    };

    var result = moneyFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      function (match, key) {
        var value = placeholderMap[key];
        return typeof value === "string" ? value : match;
      }
    );

    // Defensive cache reset if it grows too large
    if (Object.keys(formatMoneyCache).length >= FORMAT_MONEY_CACHE_MAX) {
      formatMoneyCache = {};
    }
    formatMoneyCache[cacheKey] = result;
    return result;
  }

  // ─── Discount Calculation (Local) ────────────────────────────

  /**
   * Finds the discount flags that can be ranked, with their calculation metadata.
   * Flags suppressed by a previous ranking (ours or Liquid's) are included: they
   * are hidden because they lost, not because they don't apply, and the winner
   * can change when the variant does.
   * @param {Element} root - Container to search within
   * @returns {Array<Element>} - Flags with data-discount-type/value
   */
  function findDiscountFlags(root) {
    if (!root) return [];

    var flags = root.querySelectorAll("[data-discount-type][data-discount-value]");
    var candidates = [];

    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      if (!flag.hasAttribute("hidden") || flag.hasAttribute(SUPPRESSED_ATTR)) {
        candidates.push(flag);
      }
    }

    return candidates;
  }

  /**
   * Builds the identity of a discount flag from its calculation metadata.
   * The same discount record can be rendered in several containers (PDP gallery,
   * price area, special container above the title), so flags must be compared by
   * discount identity rather than by element reference.
   * @param {Element} flag
   * @returns {string|null} - "type|value", or null when metadata is missing
   */
  function getDiscountIdentity(flag) {
    if (!flag) return null;

    var type = flag.getAttribute("data-discount-type");
    var value = flag.getAttribute("data-discount-value");
    if (!type || !value) return null;

    return type + "|" + value;
  }

  /**
   * Leaves only the winning discount flag visible, so the flag on screen always
   * matches the struck price. Flags whose metadata could not be parsed into a
   * candidate are suppressed too — they never affect the price.
   *
   * Applies the whole target state in one pass, revealing a winner that a
   * previous ranking had suppressed. Callers must not pre-restore flags: doing
   * that and then bailing out (no winner, no price container) would leave every
   * flag visible.
   * @param {Array<Element>} flags - Rankable discount flags, in document order
   * @param {Element} winningFlag - Flag of the winning candidate
   */
  function applyBestDiscountFlag(flags, winningFlag) {
    var winningIdentity = getDiscountIdentity(winningFlag);
    if (!flags || !winningIdentity) return;

    var keptParents = [];

    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      var isWinner = false;

      if (getDiscountIdentity(flag) === winningIdentity) {
        // The winner may legitimately render in several containers, but never
        // twice in the same one — a repeat there means the same discount is
        // configured more than once.
        var parent = flag.parentNode;
        if (keptParents.indexOf(parent) === -1) {
          keptParents.push(parent);
          isWinner = true;
        }
      }

      if (isWinner) {
        if (flag.hasAttribute(SUPPRESSED_ATTR)) {
          flag.removeAttribute("hidden");
          flag.removeAttribute(SUPPRESSED_ATTR);
        }
      } else {
        flag.setAttribute(SUPPRESSED_ATTR, "");
        flag.setAttribute("hidden", "");
      }
    }
  }

  /**
   * Finds PDP containers that can own this product's discount flags.
   * Keeps lookup scoped to the current product context instead of using global galleries.
   * @param {Element} productPrice - PDP product-price element
   * @returns {Array<Element>} - Roots to search for discount flags
   */
  function getPDPDiscountFlagRoots(productPrice) {
    var roots = [];

    function addRoot(root) {
      if (root && roots.indexOf(root) === -1) {
        roots.push(root);
      }
    }

    addRoot(productPrice);

    if (!productPrice || !productPrice.closest) return roots;

    // Scope galleries to the same product to avoid pulling discount flags from
    // a different product (bundles / multiple product-price blocks in one
    // section). If either side lacks a product id, include it (legacy-safe).
    var productId = productPrice.getAttribute("data-product-id");

    var scopeSelectors = [
      "quick-add-modal, dialog",
      ".shopify-section, section",
      "main"
    ];

    for (var scopeIndex = 0; scopeIndex < scopeSelectors.length; scopeIndex++) {
      var scope = productPrice.closest(scopeSelectors[scopeIndex]);
      if (!scope) continue;

      var galleryContainers = scope.querySelectorAll(".gallery-main-container");
      for (
        var galleryIndex = 0;
        galleryIndex < galleryContainers.length;
        galleryIndex++
      ) {
        var gallery = galleryContainers[galleryIndex];
        var galleryProductId = gallery.getAttribute("data-product-id");
        if (productId && galleryProductId && galleryProductId !== productId) {
          continue;
        }
        addRoot(gallery);
      }
    }

    return roots;
  }

  /**
   * Parses a discount candidate from a flag element.
   * @param {Element} flag - Flag with data-discount-type/value
   * @param {number} originalCents - Original price in cents
   * @returns {Object|null} - Candidate with finalPriceUnits, type, value; null if invalid
   */
  function parseDiscountCandidate(flag, originalCents) {
    if (!flag || originalCents <= 0) return null;

    var type = flag.getAttribute("data-discount-type");
    var valueStr = flag.getAttribute("data-discount-value");

    if (!type || !valueStr) return null;

    var value = parseFloat(valueStr);
    if (isNaN(value) || value <= 0) return null;

    // Work entirely in integer cents to match Shopify's per-line rounding.
    // Shopify rounds the discount amount to the nearest cent, then subtracts it
    // from the line price — computing on float money units drifts by ±1 cent.
    var finalCents;

    if (type === "percentage") {
      var discountCents = Math.round((originalCents * value) / 100);
      finalCents = originalCents - discountCents;
    } else if (type === "fixed_amount") {
      // fixed_amount: discount_value is already in cents (product-level discount)
      finalCents = originalCents - Math.round(value);
    } else {
      // Unknown discount type
      return null;
    }

    // Validate: must be positive and lower than original
    if (finalCents <= 0 || finalCents >= originalCents) {
      return null;
    }

    return {
      type: type,
      value: value,
      finalPriceCents: finalCents,
      finalPriceUnits: finalCents / 100,
      flag: flag
    };
  }

  /**
   * Lists the discounts that apply to a product, then resolves the best one.
   * Shared by PLP and PDP so both surfaces enumerate and rank discounts through
   * exactly the same path — the only difference is which roots they search.
   * @param {Array<Element>} roots - Containers that can hold this product's flags
   * @param {number} originalCents - Current variant price in cents
   * @param {number} compareAtCents - Manual compare-at price in cents
   * @returns {{flags: Array<Element>, best: Object|null}}
   */
  function resolveBestDiscount(roots, originalCents, compareAtCents) {
    var flags = [];

    for (var r = 0; r < roots.length; r++) {
      var rootFlags = findDiscountFlags(roots[r]);
      for (var f = 0; f < rootFlags.length; f++) {
        if (flags.indexOf(rootFlags[f]) === -1) {
          flags.push(rootFlags[f]);
        }
      }
    }

    var candidates = [];
    for (var i = 0; i < flags.length; i++) {
      var candidate = parseDiscountCandidate(flags[i], originalCents);
      if (candidate) {
        candidates.push(candidate);
      }
    }

    return {
      flags: flags,
      best: chooseBestFinalPrice(candidates, originalCents, compareAtCents)
    };
  }

  /**
   * Chooses the best valid final price from candidates and manual compare-at.
   * @param {Array<Object>} candidates - Parsed discount candidates
   * @param {number} originalCents - Original price in cents
   * @param {number} compareAtCents - Manual compare-at price in cents
   * @returns {Object|null} - Best result with finalPriceUnits and source; null if no valid discount
   */
  function chooseBestFinalPrice(candidates, originalCents, compareAtCents) {
    var bestCandidate = null;
    var bestCents = null;

    // Find lowest valid automatic candidate (all values in integer cents)
    if (candidates && candidates.length > 0) {
      for (var i = 0; i < candidates.length; i++) {
        var candidate = candidates[i];
        if (candidate.finalPriceCents < originalCents) {
          if (bestCents === null || candidate.finalPriceCents < bestCents) {
            bestCents = candidate.finalPriceCents;
            bestCandidate = candidate;
          }
        }
      }
    }

    // Manual compare-at pricing: the current price already IS the sale price
    var hasManualDiscount = compareAtCents > 0 && compareAtCents > originalCents;

    // Choose winner
    if (bestCandidate && bestCents !== null) {
      // An automatic candidate is always below the current price, so it beats
      // the manual sale price. Keep the comparison explicit for clarity.
      if (hasManualDiscount && bestCents >= originalCents) {
        return {
          finalPriceCents: originalCents,
          finalPriceUnits: originalCents / 100,
          source: "manual"
        };
      }
      return {
        finalPriceCents: bestCents,
        finalPriceUnits: bestCents / 100,
        source: "automatic",
        candidate: bestCandidate
      };
    } else if (hasManualDiscount) {
      // Only manual discount
      return {
        finalPriceCents: originalCents,
        finalPriceUnits: originalCents / 100,
        source: "manual"
      };
    }

    // No valid discount
    return null;
  }

  // ─── DOM Helpers ─────────────────────────────────────────────

  /**
   * Resets price container classes from previous runs.
   */
  function resetPriceContainerClasses(priceContainer) {
    if (!priceContainer) return;

    var contaminatedCompareEls = priceContainer.querySelectorAll(
      ".price--compare-hidden"
    );
    for (var ce = 0; ce < contaminatedCompareEls.length; ce++) {
      contaminatedCompareEls[ce].classList.remove("price--compare-hidden");
    }

    var contaminatedAutoEls = priceContainer.querySelectorAll(
      ".price--compare-auto"
    );
    for (var ae = 0; ae < contaminatedAutoEls.length; ae++) {
      contaminatedAutoEls[ae].classList.remove("price--compare-auto");
    }
  }

  /**
   * Hides a badge element if it exists.
   */
  function hideBadge(badgeEl) {
    if (badgeEl) badgeEl.setAttribute("hidden", "");
  }

  /**
   * Gets the money format from theme config.
   * Uses non-API __moneyFormat global exposed in layout/theme.liquid.
   */
  function getMoneyFormat() {
    // Primary: non-API money format global
    if (window.__moneyFormat) {
      return window.__moneyFormat;
    }
    // Default fallback
    return "{{amount}}";
  }

  /**
   * Gets the card price container (supports product-card-simple and price.liquid structures).
   */
  function getCardPriceContainer(card) {
    return card.querySelector("[data-price-container]") ||
           card.querySelector('[ref="priceContainer"]');
  }

  /**
   * Shows discounted price and strikes original.
   * The base/current price is always the struck reference: the automatic
   * discount's percentage is calculated against it, so any other reference
   * (e.g. a manual compare-at) would make the displayed math not add up.
   */
  function showDiscountedPrice(
    priceContainer,
    finalPriceUnits,
    moneyFormat,
    currentPriceEl
  ) {
    var autoDiscountPriceEl = priceContainer.querySelector(
      "[data-auto-discount-price]"
    );
    if (!currentPriceEl) {
      currentPriceEl = priceContainer.querySelector(
        "[data-original-price], .price"
      );
    }

    if (!currentPriceEl || !autoDiscountPriceEl) return;

    // Strike the current price as the reference.
    currentPriceEl.classList.add("price--compare-auto");

    // Show automatic price
    autoDiscountPriceEl.textContent = formatMoney(finalPriceUnits, moneyFormat);
    autoDiscountPriceEl.removeAttribute("hidden");
  }

  /**
   * Restores manual sale badge to its Liquid-rendered visible state.
   */
  function syncManualSaleBadge(card) {
    if (!card) return;

    var staticBadge = card.querySelector("[data-discount-badge]");
    if (!staticBadge) return;

    var compareAtPriceValue = parseInt(
      card.getAttribute("data-compare-at-price") || "0",
      10
    );
    var productPriceValue = parseInt(
      card.getAttribute("data-product-price") || "0",
      10
    );

    if (compareAtPriceValue > productPriceValue) {
      staticBadge.removeAttribute("hidden");
    }
  }

  /**
   * Resets automatic discount visual state for a card.
   */
  function resetCardDiscountState(card) {
    if (!card) return;

    var priceContainer = getCardPriceContainer(card);

    if (priceContainer) {
      resetPriceContainerClasses(priceContainer);

      var autoDiscountPriceEl = priceContainer.querySelector(
        "[data-auto-discount-price]"
      );
      if (autoDiscountPriceEl) {
        autoDiscountPriceEl.textContent = "";
        autoDiscountPriceEl.setAttribute("hidden", "");
      }
    }

    hideBadge(card.querySelector("[data-auto-discount-badge]"));

    // Flag visibility is deliberately NOT reset here: applyBestDiscountFlag owns
    // it end to end, so a bail-out below can never leave every flag visible.
    syncManualSaleBadge(card);
  }

  /**
   * Resets automatic discount visual state for a PDP price block.
   * Mirrors resetCardDiscountState so both surfaces start from the same state.
   * @param {Element} priceContainer - PDP [ref="priceContainer"]
   */
  function resetPDPDiscountState(priceContainer) {
    if (!priceContainer) return;

    resetPriceContainerClasses(priceContainer);

    var autoDiscountPriceEl = priceContainer.querySelector(
      "[data-auto-discount-price]"
    );
    if (autoDiscountPriceEl) {
      autoDiscountPriceEl.textContent = "";
      autoDiscountPriceEl.setAttribute("hidden", "");
    }

    hideBadge(priceContainer.querySelector("[data-auto-discount-badge-pdp]"));

    // Restore manual compare-at price visibility
    var manualCompareEl = priceContainer.querySelector(".compare-at-price");
    if (manualCompareEl) {
      manualCompareEl.classList.remove("price--compare-hidden");
    }
  }

  // ─── Apply to PLP Card ───────────────────────────────────────

  /**
   * Applies local discount calculation to a PLP card.
   * Reads visible flags, calculates best price, updates DOM.
   * @param {Element} card - product-card element
   */
  function applyLocalDiscountToCard(card) {
    if (!card) return;

    var moneyFormat = getMoneyFormat();

    // Get price data from card attributes (cents)
    var originalCents = parseInt(
      card.getAttribute("data-product-price") || "0",
      10
    );
    var compareAtCents = parseInt(
      card.getAttribute("data-compare-at-price") || "0",
      10
    );

    // Reset state BEFORE validating (prevents stale UI from previous run)
    resetCardDiscountState(card);

    if (originalCents <= 0) return;

    // List this product's discounts and resolve the best one
    var resolved = resolveBestDiscount([card], originalCents, compareAtCents);
    var best = resolved.best;

    if (!best) {
      // No valid discount - done
      return;
    }

    var priceContainer = getCardPriceContainer(card);
    if (!priceContainer) return;

    if (best.source === "automatic") {
      // Automatic discount wins

      // Hide static manual sale badge
      var staticBadge = card.querySelector("[data-discount-badge]");
      hideBadge(staticBadge);

      // The automatic discount's percentage is calculated against the current
      // price, not the manual compare-at, so hide the manual compare-at (if
      // any) to avoid showing a struck price whose math doesn't match the badge.
      var manualCompareEl = priceContainer.querySelector(
        ".product-card-simple__price-compare:not([data-auto-discount-price]), .compare-at-price:not([data-auto-discount-price])"
      );
      if (manualCompareEl) {
        manualCompareEl.classList.add("price--compare-hidden");
      }

      // Show automatic price and strike the current/base price.
      showDiscountedPrice(priceContainer, best.finalPriceUnits, moneyFormat);

      // A product can be covered by several active discounts, and Liquid renders
      // a flag for each. Only the winning one may stay visible, otherwise the
      // card shows two flags for a single struck price.
      applyBestDiscountFlag(resolved.flags, best.candidate.flag);

      // Auto-discount badge stays hidden: the winning flag covers this already
      hideBadge(card.querySelector("[data-auto-discount-badge]"));

      // Emit custom-price sync event
      document.dispatchEvent(
        new CustomEvent("custom-price:sync", {
          detail: {
            card: card,
            effectivePrice: Math.round(best.finalPriceUnits * 100)
          }
        })
      );
    } else if (best.source === "manual") {
      // Manual discount wins - syncManualSaleBadge already handled it
      syncManualSaleBadge(card);
    }
  }

  // ─── Apply to PDP ─────────────────────────────────────────────

  /**
   * Applies local discount calculation to PDP price block.
   * @param {Element} productPrice - product-price element
   */
  function applyLocalDiscountToPDP(productPrice) {
    if (!productPrice) return;

    var moneyFormat = getMoneyFormat();

    // Get price data from product-price attributes (cents)
    var originalCents = parseInt(
      productPrice.getAttribute("data-product-price") || "0",
      10
    );
    var compareAtCents = parseInt(
      productPrice.getAttribute("data-compare-at-price") || "0",
      10
    );

    // Flag roots for the current PDP context (gallery + price area)
    var flagRoots = getPDPDiscountFlagRoots(productPrice);
    var priceContainer = productPrice.querySelector('[ref="priceContainer"]');

    // Reset state BEFORE validating (prevents stale UI from previous run)
    resetPDPDiscountState(priceContainer);

    if (originalCents <= 0) return;

    // List this product's discounts and resolve the best one
    var resolved = resolveBestDiscount(flagRoots, originalCents, compareAtCents);
    var best = resolved.best;

    if (!best) {
      // No valid discount
      return;
    }

    if (best.source === "automatic") {
      // Automatic discount wins

      // The automatic discount's percentage is calculated against the current
      // price, not the manual compare-at, so hide the manual compare-at (if
      // any) to avoid showing a struck price whose math doesn't match the badge.
      var manualCompareEl = priceContainer
        ? priceContainer.querySelector(".compare-at-price")
        : null;
      if (manualCompareEl) {
        manualCompareEl.classList.add("price--compare-hidden");
      }

      // Show automatic price
      if (priceContainer) {
        showDiscountedPrice(priceContainer, best.finalPriceUnits, moneyFormat);
      }

      // A product can be covered by several active discounts, and Liquid renders
      // a flag for each. Only the winning one may stay visible, otherwise the
      // PDP shows two flags for a single struck price.
      applyBestDiscountFlag(resolved.flags, best.candidate.flag);

      // Emit custom-price sync for PDP
      var customPriceEl = productPrice.querySelector(".custom-price");
      if (customPriceEl) {
        var configStr = customPriceEl.getAttribute("data-custom-price-config");
        if (configStr) {
          try {
            var customConfig = JSON.parse(configStr);
            if (
              customConfig.accumulates === true &&
              typeof best.finalPriceUnits === "number"
            ) {
              document.dispatchEvent(
                new CustomEvent("custom-price:sync-pdp", {
                  detail: {
                    host: productPrice,
                    effectivePrice: Math.round(best.finalPriceUnits * 100)
                  }
                })
              );
            }
          } catch (e) {
            console.warn("[AutoDiscounts] Invalid data-custom-price-config JSON:", e);
          }
        }
      }
    } else if (best.source === "manual") {
      // Manual discount wins - compare-at already visible
    }
  }

  /**
   * Whether a product-price element is a PDP price block rather than a PLP
   * card's. Running the PDP path against a card strips the card's strikethrough.
   * @param {Element} el
   * @returns {boolean}
   */
  function isPDPPriceBlock(el) {
    if (!el || !el.matches || !el.matches("product-price[data-variant-id]")) {
      return false;
    }

    return !el.closest("product-card");
  }

  // ─── Initialization PLP ──────────────────────────────────────

  /**
   * Processes all initial PLP cards on page load.
   */
  function processInitialCards() {
    var cards = document.querySelectorAll("product-card[data-variant-id]");
    for (var i = 0; i < cards.length; i++) {
      applyLocalDiscountToCard(cards[i]);
    }
  }

  // ─── MutationObserver (Injected Content) ─────────────────────

  var debounceTimer = null;
  var priceBlockFrame = null;
  var pendingMutationCards = [];
  var pendingProductPrices = [];

  /**
   * Gets the container to observe for injected content.
   * Page-level wrappers come first on purpose: a product grid would exclude the
   * main product section, whose HTML the colour picker swaps wholesale — and a
   * PDP with a recommendations grid does have one.
   */
  function getProductContainer() {
    var containers = [
      document.querySelector("#MainContent"),
      document.querySelector("main"),
      document.querySelector(".product-grid"),
      document.querySelector("[product-grid]")
    ];

    for (var i = 0; i < containers.length; i++) {
      if (containers[i]) return containers[i];
    }

    return document.body;
  }

  /**
   * Handles newly inserted cards after debounce.
   */
  function flushPendingCards() {
    var cards = pendingMutationCards.slice();
    pendingMutationCards = [];

    // Dedupe by element reference using indexOf (safe for DOM elements)
    var uniqueCards = [];
    for (var i = 0; i < cards.length; i++) {
      if (uniqueCards.indexOf(cards[i]) === -1) {
        uniqueCards.push(cards[i]);
      }
    }

    for (var j = 0; j < uniqueCards.length; j++) {
      applyLocalDiscountToCard(uniqueCards[j]);
    }
  }

  /**
   * Handles newly inserted PDP price blocks on the next frame.
   * Not debounced like cards: a swapped price block is a single visible element,
   * and waiting would show the flag without its struck price.
   */
  function flushPendingProductPrices() {
    priceBlockFrame = null;

    var prices = pendingProductPrices.slice();
    pendingProductPrices = [];

    var unique = [];
    for (var i = 0; i < prices.length; i++) {
      if (unique.indexOf(prices[i]) === -1) {
        unique.push(prices[i]);
      }
    }

    for (var j = 0; j < unique.length; j++) {
      // The node can be replaced again before the frame runs
      if (unique[j].isConnected) {
        applyLocalDiscountToPDP(unique[j]);
      }
    }
  }

  /**
   * Queues a newly inserted node, and anything relevant inside it.
   */
  function queueInjectedNode(node) {
    if (node.matches && node.matches("product-card[data-variant-id]")) {
      pendingMutationCards.push(node);
    }

    if (node.matches && isPDPPriceBlock(node)) {
      pendingProductPrices.push(node);
    }

    if (!node.querySelectorAll) return;

    var innerCards = node.querySelectorAll("product-card[data-variant-id]");
    for (var i = 0; i < innerCards.length; i++) {
      pendingMutationCards.push(innerCards[i]);
    }

    var innerPrices = node.querySelectorAll("product-price[data-variant-id]");
    for (var j = 0; j < innerPrices.length; j++) {
      if (isPDPPriceBlock(innerPrices[j])) {
        pendingProductPrices.push(innerPrices[j]);
      }
    }
  }

  /**
   * Observes dynamically injected product cards (infinite scroll, filters) and
   * PDP price blocks. The colour picker swaps the whole product section without
   * dispatching shopify:product:select, so this is what recalculates its price.
   */
  function observeInjectedContent() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          if (addedNodes[j].nodeType === 1) {
            queueInjectedNode(addedNodes[j]);
          }
        }
      }

      if (pendingProductPrices.length > 0 && priceBlockFrame === null) {
        priceBlockFrame = requestAnimationFrame(flushPendingProductPrices);
      }

      if (pendingMutationCards.length === 0) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flushPendingCards, MUTATION_DEBOUNCE);
    });

    var container = getProductContainer();
    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Variant Update Handler (PDP) ────────────────────────────

  /**
   * Updates variant attributes on card when selection changes.
   */
  function syncCardVariantAttributes(card, resource) {
    if (!card || !resource) return;

    if (resource.id != null) {
      card.setAttribute("data-variant-id", String(resource.id));
    }

    if (resource.price != null) {
      card.setAttribute("data-product-price", String(resource.price));
    }

    card.setAttribute(
      "data-compare-at-price",
      String(resource.compare_at_price || 0)
    );
  }

  /**
   * Handles shopify:product:select (PLP cards and PDP). Recibe el `detail` ya aplanado
   * que publica variant-picker.js al resolver la promesa del evento.
   */
  function handleThemeVariantUpdate(event) {
    var detail = event.detail;
    var target = event.target;

    // PLP card variant change
    var card =
      target && target.closest ? target.closest("product-card") : null;

    if (card && detail && detail.resource) {
      syncCardVariantAttributes(card, detail.resource);

      requestAnimationFrame(function () {
        applyLocalDiscountToCard(card);
      });
      return;
    }

    // PDP variant change - variant-picker dispatches the event, so we need to find product-price in DOM
    // NOT via event.target.closest("product-price") because variant-picker is not inside product-price
    if (!detail || !detail.resource) return;

    // Find PDP product-price element using productId from event detail.data
    var productId = detail.productId || null;
    var productPrice = null;

    if (target && target.closest) {
      var scope = target.closest(
        "section, product-info, product-form-component, quick-add-modal, dialog, .shopify-section, main"
      );
      if (scope) {
        if (productId) {
          productPrice = scope.querySelector(
            "product-price[data-product-id=\"" + productId + "\"]"
          );
        }

        if (!productPrice) {
          var scopedProductPrices = scope.querySelectorAll(
            "product-price[data-variant-id]"
          );
          if (scopedProductPrices.length === 1) {
            productPrice = scopedProductPrices[0];
          }
        }
      }
    }

    if (productId) {
      var matchingProductPrices = document.querySelectorAll(
        "product-price[data-product-id=\"" + productId + "\"]"
      );
      if (!productPrice && matchingProductPrices.length === 1) {
        productPrice = matchingProductPrices[0];
      }
    }

    // Fallback: single product-price context (safe when only one exists)
    // Do NOT use if multiple product-price elements could be present
    if (!productPrice) {
      var allProductPrices = document.querySelectorAll("product-price[data-variant-id]");
      // Safe fallback only when single instance exists
      if (allProductPrices.length === 1) {
        productPrice = allProductPrices[0];
      }
    }

    if (!productPrice) return;

    // Update product-price attributes
    if (detail.resource.id != null) {
      productPrice.setAttribute("data-variant-id", String(detail.resource.id));
    }
    if (detail.resource.price != null) {
      productPrice.setAttribute(
        "data-product-price",
        String(detail.resource.price)
      );
    }
    productPrice.setAttribute(
      "data-compare-at-price",
      String(detail.resource.compare_at_price || 0)
    );

    // Update gallery variant ID if present
    var galleryContainer = document.querySelector(
      ".gallery-main-container[data-variant-id]"
    );
    if (galleryContainer && detail.resource.id != null) {
      galleryContainer.setAttribute(
        "data-variant-id",
        String(detail.resource.id)
      );
    }

    // Recalculate PDP discount
    requestAnimationFrame(function () {
      applyLocalDiscountToPDP(productPrice);
    });
  }

  // ─── Initialization PDP ──────────────────────────────────────

  /**
   * Processes PDP on initial load.
   */
  function processPDP() {
    var productPrice = document.querySelector("product-price[data-variant-id]");
    // On PLP pages the first grid card's price block matches this selector
    if (!isPDPPriceBlock(productPrice)) return;

    applyLocalDiscountToPDP(productPrice);
  }

  // ─── Arranque ────────────────────────────────────────────────

  /**
   * Main initialization.
   */
  function init() {
    processInitialCards();
    processPDP();
    observeInjectedContent();
    // Horizon 4.0.0 retiro `variant:update`: variant-picker.js ahora emite el evento estandar
    // `shopify:product:select`, cuyos datos internos viajan en la promesa. La doc de Shopify
    // define `detail` como "custom data for the storefront's internal use; listeners can read it",
    // y Horizon publica ahi los mismos campos que llevaba el evento viejo.
    // Este archivo no es un modulo ES, asi que se escucha por nombre literal, sin import.
    document.addEventListener(
      "shopify:product:select",
      function (event) {
        if (!event.promise) return;
        event.promise
          .then(function (result) {
            var d = result && result.detail;
            if (!d) return;
            handleThemeVariantUpdate({ detail: d, target: event.target });
          })
          .catch(function () {});
      },
      true
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
